import { z } from "zod";

import { videoKinds } from "./videoKinds";

import type { RoundMemberRole } from "@engine/round/actions";
import type {
  Funder,
  Proposal,
  ProposalForm,
  Recommender,
  RoundMember,
} from "@engine/round/aggregate";
import type {
  SProcessRoundMember as DBRoundMember,
  VideoType,
} from "@sfc/database";

export type Evaluable = Recommender | Proposal;
export type Evaluator = Recommender | Funder;

// Enum order is the canonical display order for track lists/groups everywhere
// (sidebar rail, transcript grouping, evaluation grids).
export const trackSchema = z.enum(["MAIN", "FAIRNESS", "FREEDOM", "NO_TRACK"]);

export const trackNames: Record<Track, string> = {
  MAIN: "Main",
  FAIRNESS: "Fairness",
  FREEDOM: "Freedom",
  NO_TRACK: "No Track",
};

export type Track = z.infer<typeof trackSchema>;

// The round-side tracks a transcript track can be associated with. Derived from
// trackSchema so it stays in sync with the canonical enum; NO_TRACK is excluded
// because it's an "unassigned" state, not a real track to map onto.
export const assignableTrackSchema = trackSchema.exclude(["NO_TRACK"]);

export type AssignableTrack = z.infer<typeof assignableTrackSchema>;

// Mirrors the `VideoType` enum in
// `packages/database/prisma/schema/s-process.prisma`. Enum order is the
// canonical display order for video-type pickers. The iPhone bundle keeps its
// own copy in `client/iphone/lib/videoTypeLabels.ts` to avoid importing this
// module's types — keep the two in sync.
export const videoTypeSchema = z.enum([
  "RAW_RECORDING",
  "EXCERPT",
  "MEETING_CUT",
  "TRACK_OVERVIEW",
  "RECOMMENDER_OVERVIEW",
  "ROUND_OVERVIEW",
  "ONBOARDING",
  "EXPLAINER",
]);

// The singular display names, projected out of the `videoKinds` table so the
// labels have ONE home. Kept as its own export because a lot of call sites
// (admin pickers, card subtitles) already read this shape. For a plural group
// heading use `videoKindGroupLabel` instead — this map is singular only.
export const videoTypeNames: Record<VideoType, string> = Object.fromEntries(
  Object.entries(videoKinds).map(([type, kind]) => [type, kind.label]),
) as Record<VideoType, string>;

// Narrow a nullable track-column value (a plain string in the DB) to the
// AssignableTrack enum. Returns null for null/unknown values.
export function isAssignableTrack(
  value: string | null | undefined,
): value is AssignableTrack {
  return value != null && assignableTrackSchema.safeParse(value).success;
}

export type UserProfile = {
  id: string;
  name: string | null;
  email: string;
  isSfcAdmin: boolean;
  track: Track;
  deanonymize: DBRoundMember["deanonymize"];
};

export type UserProfiles = {
  [userId: string]: UserProfile;
};

export type ProposalAndForm = {
  proposalForm: ProposalForm;
  proposal: Proposal;
};

export type AppMode = {
  adminCanEdit: boolean;
  viewAsTrackMember: Track | null;
  viewAsUserId: string | null;
  /**
   * Set once the socket is torn down for good (engine out of date, schema
   * skew, session rejected). Editing is refused while it is set — read that
   * through the store's derived `editLock`, not this flag.
   */
  disconnected: boolean;
};

export type RoundMemberAndProfile = RoundMember & {
  roles: RoundMemberRole[];
  budget?: number;
  profile?: UserProfile;
};

declare global {
  type DisconnectDescription =
    | Error
    | {
        description: string;
        context?: unknown;
      };
}
